import { useState, useEffect } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import { api } from "../api";
import {
  Activity, DollarSign, Zap, TrendingUp, Loader2, Coins,
} from "lucide-react";

const COLORS = ["#005F71", "#217D84", "#CC4B37", "#FFD26F", "#9333ea", "#0891b2"];

export default function Tokens() {
  const [summary, setSummary] = useState<any>(null);
  const [byModel, setByModel] = useState<any[]>([]);
  const [daily, setDaily] = useState<any[]>([]);
  const [recent, setRecent] = useState<any[]>([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.tokenSummary(days),
      api.tokensByModel(days),
      api.tokensDaily(days),
      api.tokensRecent(50),
    ]).then(([s, bm, d, r]) => {
      setSummary(s);
      setByModel(bm);
      setDaily(d);
      setRecent(r);
      setLoading(false);
    });
  }, [days]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-novacare-500" />
      </div>
    );
  }

  const cards = [
    {
      label: "Total Requests",
      value: summary?.total_requests?.toLocaleString() || "0",
      icon: Zap,
      color: "blue",
    },
    {
      label: "Total Tokens",
      value: summary?.total_tokens?.toLocaleString() || "0",
      icon: Activity,
      color: "purple",
    },
    {
      label: "Total Cost",
      value: `$${(summary?.total_cost || 0).toFixed(4)}`,
      icon: DollarSign,
      color: "green",
    },
    {
      label: "Tool Calls",
      value: summary?.total_tool_calls?.toLocaleString() || "0",
      icon: Coins,
      color: "orange",
    },
  ];

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Token Usage</h1>
          <p className="text-slate-500 mt-1">LLM token observability & cost tracking</p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="border border-slate-300 rounded-lg px-4 py-2 text-sm"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {summary?.total_requests === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600 mb-2">No token usage yet</h3>
          <p className="text-slate-400">
            Chat with the AI assistant to start tracking token usage.
          </p>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {cards.map((c) => (
              <div
                key={c.label}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`p-2 rounded-lg bg-${c.color}-50`}>
                    <c.icon className={`w-5 h-5 text-${c.color}-600`} />
                  </div>
                </div>
                <p className="text-sm text-slate-500 mb-1">{c.label}</p>
                <p className="text-2xl font-bold text-slate-900">{c.value}</p>
              </div>
            ))}
          </div>

          {/* Detailed stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatBox label="Input Tokens" value={summary?.total_input} />
            <StatBox label="Output Tokens" value={summary?.total_output} />
            <StatBox label="Cache Read" value={summary?.total_cache_read} />
            <StatBox label="Avg Duration" value={`${summary?.avg_duration_ms || 0}ms`} />
          </div>

          {/* Daily usage chart */}
          {daily.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">
                Daily Token Usage
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={daily}>
                  <defs>
                    <linearGradient id="tokenGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#005F71" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#005F71" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="tokens"
                    stroke="#005F71"
                    fill="url(#tokenGrad)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* By model */}
          {byModel.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  Usage by Model
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={byModel} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" stroke="#64748b" />
                    <YAxis
                      type="category"
                      dataKey="model"
                      stroke="#64748b"
                      width={120}
                    />
                    <Tooltip />
                    <Bar dataKey="total" fill="#005F71" name="Tokens" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  Cost by Model
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={byModel}
                      dataKey="cost"
                      nameKey="model"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={(e) => e.model}
                    >
                      {byModel.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Recent records table */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              Recent Requests
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Time</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Model</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-600">Input</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-600">Output</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-600">Total</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-600">Tools</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-600">Duration</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-600">Cost</th>
                    <th className="text-center py-2 px-3 font-medium text-slate-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 text-slate-600 text-xs">
                        {new Date(r.created_at).toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-slate-700 font-medium">
                        {r.model}
                      </td>
                      <td className="py-2 px-3 text-right text-slate-600">
                        {r.input.toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-right text-slate-600">
                        {r.output.toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-right font-medium text-slate-900">
                        {r.total.toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-right text-slate-600">
                        {r.tool_calls}
                      </td>
                      <td className="py-2 px-3 text-right text-slate-600">
                        {r.duration_ms}ms
                      </td>
                      <td className="py-2 px-3 text-right text-slate-600">
                        ${(r.cost_total).toFixed(6)}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <span
                          className={`px-2 py-0.5 rounded text-xs ${
                            r.success
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {r.success ? "✓" : "✗"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatBox({ label, value }: { label: string; value: any }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className="text-lg font-bold text-slate-900">
        {typeof value === "number" ? value.toLocaleString() : value}
      </p>
    </div>
  );
}